import React, { useEffect, useMemo, useState } from "react";
import Button from "./Button";
import Input from "./Input";

function getValueByPath(item, path) {
  if (!item || !path) return undefined;
  if (!path.includes(".")) return item[path];

  return path.split(".").reduce((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return acc[key];
  }, item);
}

function formatCellValue(value, emptyValue) {
  if (value === null || value === undefined) return emptyValue;
  if (typeof value === "string" && value.trim() === "") return emptyValue;

  const formatWithCommas = (numericValue) => numericValue.toLocaleString("en-US");

  if (typeof value === "number" && Number.isFinite(value) && Math.abs(value) >= 1000) {
    return formatWithCommas(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const numericValue = Number(trimmed);
      if (Number.isFinite(numericValue) && Math.abs(numericValue) >= 1000) {
        return formatWithCommas(numericValue);
      }
    }
  }

  return value;
}

function DataTable({
  columns = [],
  data = [],
  isLoading = false,
  pagination,
  onPageChange,
  onSort,
  sortConfig,
  searchable = true,
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  pageSize = 10,
  sortable = true,
  paginated = true,
  scrollHeight = "75vh",
  enableFieldSearch = false,
  fieldSearchColumns = [],
  fieldSearchPlaceholder = "Search in field...",
  enableDateFilter = false,
  dateFilterColumnKey = "",
  dateFilterLabel = "Date range",
  enableDropdownFilters = false,
  dropdownFilters = [],
  toolbarFilters,
  toolbarAction,
  emptyValue = "-",
  noDataMessage = "No records found",
  expandable = false,
  expandedRows = new Set(),
  renderExpandableContent = null,
  onRowClick = null,
  renderMobileCard = null,
  rowKeyField = "id",
}) {
  const rows = useMemo(() => (Array.isArray(data) ? data : data?.data || []), [data]);
  const isControlledPagination = Boolean(pagination);
  const isControlledSort = typeof onSort === "function" && Boolean(sortConfig);
  const isControlledSearch = typeof onSearchChange === "function";

  const [localSearch, setLocalSearch] = useState(searchValue || "");
  const [localSortConfig, setLocalSortConfig] = useState(() => {
    const defaultColumn = sortable ? columns.find((column) => column.sortable) : null;
    return defaultColumn ? { key: defaultColumn.key, order: "ASC" } : { key: "", order: "ASC" };
  });
  const [localPage, setLocalPage] = useState(1);
  const [fieldSearchValues, setFieldSearchValues] = useState({});
  const [activeFieldSearchColumns, setActiveFieldSearchColumns] = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dropdownValues, setDropdownValues] = useState({});

  useEffect(() => {
    if (typeof searchValue === "string") {
      setLocalSearch(searchValue);
    }
  }, [searchValue]);

  const resolvedSearch = isControlledSearch ? searchValue || "" : localSearch;
  const resolvedSort = isControlledSort ? sortConfig : localSortConfig;
  const resolvedFieldSearchColumns = useMemo(() => {
    if (!Array.isArray(fieldSearchColumns) || fieldSearchColumns.length === 0) return [];
    return fieldSearchColumns.filter((item) => item?.key);
  }, [fieldSearchColumns]);
  const fieldSearchColumnKeySet = useMemo(
    () => new Set(resolvedFieldSearchColumns.map((column) => column.key)),
    [resolvedFieldSearchColumns]
  );
  const hasActiveHeaderFilters = useMemo(
    () => Object.values(activeFieldSearchColumns).some(Boolean),
    [activeFieldSearchColumns]
  );

  const filteredRows = useMemo(() => {
    const globalQuery = resolvedSearch.trim().toLowerCase();
    const fromTime = dateFrom ? new Date(dateFrom).setHours(0, 0, 0, 0) : null;
    const toTime = dateTo ? new Date(dateTo).setHours(23, 59, 59, 999) : null;

    return rows.filter((item) => {
      if (globalQuery) {
        const matchGlobal = columns.some((column) => {
          const rawValue = column.searchAccessor
            ? column.searchAccessor(item)
            : getValueByPath(item, column.key);
          if (rawValue === null || rawValue === undefined) return false;
          return String(rawValue).toLowerCase().includes(globalQuery);
        });
        if (!matchGlobal) return false;
      }

      if (enableFieldSearch && resolvedFieldSearchColumns.length) {
        const hasFieldMismatch = resolvedFieldSearchColumns.some((column) => {
          if (!activeFieldSearchColumns[column.key]) return false;
          const query = String(fieldSearchValues[column.key] || "").trim().toLowerCase();
          if (!query) return false;
          const fieldValue = getValueByPath(item, column.key);
          if (fieldValue === null || fieldValue === undefined) return true;
          return !String(fieldValue).toLowerCase().includes(query);
        });
        if (hasFieldMismatch) return false;
      }

      if (enableDateFilter && dateFilterColumnKey && (fromTime || toTime)) {
        const rawDateValue = getValueByPath(item, dateFilterColumnKey);
        const parsed = rawDateValue ? new Date(rawDateValue) : null;
        if (!parsed || Number.isNaN(parsed.getTime())) return false;
        const currentTime = parsed.getTime();
        if (fromTime !== null && currentTime < fromTime) return false;
        if (toTime !== null && currentTime > toTime) return false;
      }

      if (enableDropdownFilters && dropdownFilters.length) {
        const hasDropdownMismatch = dropdownFilters.some((filterConfig) => {
          const selected = dropdownValues[filterConfig.key];
          if (!selected) return false;
          const value = filterConfig.accessor
            ? filterConfig.accessor(item)
            : getValueByPath(item, filterConfig.key);
          return String(value ?? "") !== String(selected);
        });
        if (hasDropdownMismatch) return false;
      }

      return true;
    });
  }, [
    columns,
    dateFilterColumnKey,
    dateFrom,
    dateTo,
    dropdownFilters,
    dropdownValues,
    enableDateFilter,
    enableDropdownFilters,
    enableFieldSearch,
    activeFieldSearchColumns,
    fieldSearchValues,
    resolvedSearch,
    resolvedFieldSearchColumns,
    rows,
  ]);

  const sortedRows = useMemo(() => {
    if (!sortable || !resolvedSort?.key) return filteredRows;
    const column = columns.find((item) => item.key === resolvedSort.key);
    if (!column?.sortable) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const aValue = column.sortAccessor ? column.sortAccessor(a) : getValueByPath(a, resolvedSort.key);
      const bValue = column.sortAccessor ? column.sortAccessor(b) : getValueByPath(b, resolvedSort.key);
      const av = String(aValue ?? "").toLowerCase();
      const bv = String(bValue ?? "").toLowerCase();
      if (av === bv) return 0;
      return resolvedSort.order === "ASC" ? (av > bv ? 1 : -1) : av < bv ? 1 : -1;
    });
  }, [columns, filteredRows, resolvedSort, sortable]);

  const internalPagination = useMemo(() => {
    const totalItems = sortedRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const page = Math.min(localPage, totalPages);
    return { page, limit: pageSize, totalItems, totalPages };
  }, [localPage, pageSize, sortedRows.length]);

  useEffect(() => {
    if (!isControlledPagination && localPage > internalPagination.totalPages) {
      setLocalPage(internalPagination.totalPages);
    }
  }, [internalPagination.totalPages, isControlledPagination, localPage]);

  const resolvedPagination = paginated ? (isControlledPagination ? pagination : internalPagination) : null;
  const pageStart = resolvedPagination ? (resolvedPagination.page - 1) * resolvedPagination.limit : 0;
  const visibleRows = resolvedPagination
    ? isControlledPagination
      ? rows
      : sortedRows.slice(pageStart, pageStart + resolvedPagination.limit)
    : sortedRows;

  const displayColumns = useMemo(() => {
    const srNoColumn = columns.find((c) => c.key === "sr_no");
    const otherColumns = columns.filter((c) => c.key !== "sr_no");

    return [
      {
        key: "sr_no",
        label: "Sr. No.",
        sortable: false,
        render: (_, index) => pageStart + index + 1,
        ...srNoColumn,
      },
      ...otherColumns,
    ];
  }, [columns, pageStart]);

  const handleSort = (key) => {
    if (isControlledSort) {
      onSort(key);
      return;
    }
    setLocalSortConfig((current) => ({
      key,
      order: current.key === key && current.order === "ASC" ? "DESC" : "ASC",
    }));
  };

  const handleSearch = (value) => {
    if (isControlledSearch) {
      onSearchChange(value);
    } else {
      setLocalSearch(value);
    }
    if (!isControlledPagination) {
      setLocalPage(1);
    }
  };

  const handleFieldSearchChange = (key, value) => {
    setFieldSearchValues((current) => ({ ...current, [key]: value }));
    if (!isControlledPagination) setLocalPage(1);
  };
  const handleHeaderClick = (column) => {
    if (enableFieldSearch && fieldSearchColumnKeySet.has(column.key)) {
      setActiveFieldSearchColumns((current) => {
        const isActive = Boolean(current[column.key]);
        const next = { ...current, [column.key]: !isActive };
        if (isActive) {
          setFieldSearchValues((values) => ({ ...values, [column.key]: "" }));
        }
        return next;
      });
      if (!isControlledPagination) setLocalPage(1);
      return;
    }

    if (sortable && column.sortable) {
      handleSort(column.key);
    }
  };

  const handleDateChange = (type, value) => {
    if (type === "from") setDateFrom(value);
    if (type === "to") setDateTo(value);
    if (!isControlledPagination) setLocalPage(1);
  };

  const handleDropdownChange = (key, value) => {
    setDropdownValues((current) => ({ ...current, [key]: value }));
    if (!isControlledPagination) setLocalPage(1);
  };

  const handlePageChange = (nextPage) => {
    if (isControlledPagination && onPageChange) {
      onPageChange(nextPage);
      return;
    }
    setLocalPage(nextPage);
  };

  return (
    <div className="flex min-h-0 flex-col overflow-visible rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 max-w-full">
      {/* Integrated Header: Search and Filters */}
      {(searchable || enableDateFilter || (enableDropdownFilters && dropdownFilters.length > 0) || toolbarFilters || toolbarAction) && (
        <div className="border-b border-slate-100 bg-slate-50/50 p-3 dark:border-slate-800 dark:bg-slate-900/50">
          <div className="flex flex-wrap items-center gap-3 lg:justify-between">
            <div className="flex flex-col sm:flex-row flex-1 items-stretch sm:items-center gap-3 min-w-0 w-full">
              {searchable && (
                <div className="flex-1 min-w-0 sm:min-w-[240px] lg:max-w-xs">
                  <Input
                    name="datatable-search"
                    value={resolvedSearch}
                    onChange={(event) => handleSearch(event.target.value)}
                    placeholder={searchPlaceholder}
                    className="!py-2"
                    suffix={
                      resolvedSearch && (
                        <button
                          type="button"
                          onClick={() => handleSearch("")}
                          className="flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                          title="Clear search"
                        >
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )
                    }
                  />
                </div>
              )}

              {toolbarFilters}

              {enableDateFilter && dateFilterColumnKey && (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">From:</span>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(event) => {
                        const year = event.target.value.split("-")[0];
                        if (year && year.length > 4) return;
                        handleDateChange("from", event.target.value);
                      }}
                      max="9999-12-31"
                      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">To:</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(event) => {
                        const year = event.target.value.split("-")[0];
                        if (year && year.length > 4) return;
                        handleDateChange("to", event.target.value);
                      }}
                      max="9999-12-31"
                      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  </div>
                </div>
              )}

              {enableDropdownFilters &&
                dropdownFilters.map((filterConfig) => (
                  <div key={filterConfig.key} className="flex items-center gap-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {filterConfig.label || filterConfig.key}:
                    </label>
                    <select
                      value={dropdownValues[filterConfig.key] || ""}
                      onChange={(event) => handleDropdownChange(filterConfig.key, event.target.value)}
                      className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/10 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    >
                      <option value="">All</option>
                      {(filterConfig.options || []).map((option) => {
                        const optionValue = typeof option === "object" ? option?.value : option;
                        const optionLabel = typeof option === "object" ? option?.label || option?.value : option;
                        return (
                          <option key={`${filterConfig.key}-${optionValue}`} value={optionValue}>
                            {optionLabel}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ))}
            </div>

            <div className="flex items-center justify-end gap-3 shrink-0">
              {enableFieldSearch && !hasActiveHeaderFilters && (
                <p className="hidden text-[11px] font-medium text-brand-text-secondary xl:block">
                  Click headers to filter
                </p>
              )}
              {toolbarAction && <div className="flex justify-end">{toolbarAction}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Table Content Area */}
      <div className="relative overflow-auto" style={{ maxHeight: scrollHeight }}>
        <table className={`w-full min-w-max border-collapse text-left ${renderMobileCard ? "hidden md:table" : ""}`}>
          <thead className="sticky top-0 z-10 bg-slate-50/80 backdrop-blur-md dark:bg-slate-800/80">
            <tr>
              {displayColumns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  style={column.headerStyle}
                  className={`border-b border-slate-200 px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:border-slate-700 dark:text-slate-300 ${
                    sortable && column.sortable ? "cursor-pointer select-none hover:text-primary-600" : ""
                  } ${column.headerClassName || ""}`}
                  onClick={() => handleHeaderClick(column)}
                >
                  <div className={`flex items-center gap-2 ${
                    column.headerClassName?.includes("text-right") ? "justify-end" : 
                    column.headerClassName?.includes("text-center") ? "justify-center" : "justify-start"
                  }`}>
                    <span>{column.label}</span>
                    {enableFieldSearch && fieldSearchColumnKeySet.has(column.key) && (
                      <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    )}
                    {sortable && column.sortable && resolvedSort?.key === column.key && (
                      <span className="text-primary-600">
                        {resolvedSort.order === "ASC" ? (
                          <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
            {enableFieldSearch && hasActiveHeaderFilters && (
              <tr>
                {displayColumns.map((column) => (
                  <th key={`search-${column.key}`} className="border-b border-slate-100 bg-white px-3 py-1 dark:border-slate-800 dark:bg-slate-900">
                    {fieldSearchColumnKeySet.has(column.key) && activeFieldSearchColumns[column.key] ? (
                      <input
                        type="text"
                        autoFocus
                        value={fieldSearchValues[column.key] || ""}
                        onChange={(event) => handleFieldSearchChange(column.key, event.target.value)}
                        placeholder={`Filter ${typeof column.label === "string" ? column.label : (column.key || "field")}...`}
                        className={`w-full rounded-md border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] text-slate-900 focus:outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-primary-dark dark:focus:ring-primary-dark/20 ${
                          column.headerClassName?.includes("text-right") ? "text-right" : ""
                        }`}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
            {isLoading ? (
              <tr>
                <td colSpan={displayColumns.length} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                    <p className="text-xs font-medium text-slate-500">Loading records...</p>
                  </div>
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={displayColumns.length} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <svg className="h-10 w-10 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{noDataMessage}</p>
                  </div>
                </td>
              </tr>
            ) : (
              visibleRows.map((item, index) => {
                const rowKey = item[rowKeyField] || item.id || item.sellingMasterId || index;
                const isExpanded = expandable && expandedRows.has(rowKey);
                
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      onClick={() => {
                        if (onRowClick) {
                          onRowClick(item, index);
                        }
                      }}
                      className={`group transition-colors odd:bg-white even:bg-slate-50/50 hover:bg-primary-50/30 dark:odd:bg-slate-900/50 dark:even:bg-slate-800/50 dark:hover:bg-primary-900/10 ${
                        onRowClick ? "cursor-pointer" : ""
                      }`}
                    >
                      {displayColumns.map((column) => (
                        <td
                          key={column.key}
                          style={column.style}
                          className={`whitespace-nowrap px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300 ${column.className || ""}`}
                        >
                          {column.render
                            ? column.render(item, index)
                            : formatCellValue(getValueByPath(item, column.key), emptyValue)}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && renderExpandableContent && (
                      <tr className="bg-slate-50/50 dark:bg-slate-900/50">
                        <td colSpan={displayColumns.length} className="p-0 border-b border-slate-200 dark:border-slate-800">
                          {renderExpandableContent(item)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
        {renderMobileCard && (
          <div className="block md:hidden p-3 space-y-3">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                <p className="text-xs font-medium text-slate-500">Loading records...</p>
              </div>
            ) : visibleRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12">
                <svg className="h-10 w-10 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{noDataMessage}</p>
              </div>
            ) : (
              visibleRows.map((item, index) => renderMobileCard(item, index))
            )}
          </div>
        )}
      </div>

      {/* Integrated Pagination Footer */}
      {resolvedPagination && (
        <div className="flex items-center justify-between border-t border-slate-100 bg-white px-3 py-1.5 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-1 items-center justify-between sm:hidden">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handlePageChange(resolvedPagination.page - 1)}
              disabled={resolvedPagination.page <= 1}
            >
              Previous
            </Button>
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              {resolvedPagination.page} / {resolvedPagination.totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handlePageChange(resolvedPagination.page + 1)}
              disabled={resolvedPagination.page >= resolvedPagination.totalPages}
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-xs text-brand-text-secondary dark:text-slate-400">
                Showing <span className="font-bold text-brand-text-primary dark:text-white">{(resolvedPagination.page - 1) * resolvedPagination.limit + 1}</span> to{" "}
                <span className="font-bold text-brand-text-primary dark:text-white">
                  {Math.min(resolvedPagination.page * resolvedPagination.limit, resolvedPagination.totalItems)}
                </span>{" "}
                of <span className="font-bold text-brand-text-primary dark:text-white">{resolvedPagination.totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex gap-1.5" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(resolvedPagination.page - 1)}
                  disabled={resolvedPagination.page <= 1}
                  className="relative inline-flex items-center rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition-all hover:bg-slate-50 hover:text-primary-600 disabled:opacity-40 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                <div className="flex items-center gap-1">
                  {(() => {
                    const maxButtons = 5;
                    const total = resolvedPagination.totalPages;
                    if (total <= maxButtons) {
                      return Array.from({ length: total }, (_, i) => i + 1).map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`min-w-[36px] rounded-lg px-2.5 py-2 text-xs font-bold transition-all ${
                            resolvedPagination.page === pageNum
                              ? "bg-primary-500 text-white shadow-sm"
                              : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                          }`}
                        >
                          {pageNum}
                        </button>
                      ));
                    }

                    // sliding window centered on current page
                    const half = Math.floor(maxButtons / 2);
                    let start = Math.max(1, resolvedPagination.page - half);
                    let end = start + maxButtons - 1;
                    if (end > total) {
                      end = total;
                      start = Math.max(1, end - maxButtons + 1);
                    }

                    const pages = [];
                    if (start > 1) {
                      pages.push(1);
                      if (start > 2) pages.push("ellipsis-start");
                    }

                    for (let p = start; p <= end; p++) pages.push(p);

                    if (end < total) {
                      if (end < total - 1) pages.push("ellipsis-end");
                      pages.push(total);
                    }

                    return pages.map((pageNum, idx) => {
                      if (pageNum === "ellipsis-start" || pageNum === "ellipsis-end") {
                        return (
                          <span key={`ell-${idx}`} className="px-1 text-slate-400">
                            ...
                          </span>
                        );
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`min-w-[36px] rounded-lg px-2.5 py-2 text-xs font-bold transition-all ${
                            resolvedPagination.page === pageNum
                              ? "bg-primary-500 text-white shadow-sm"
                              : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    });
                  })()}
                </div>

                <button
                  onClick={() => handlePageChange(resolvedPagination.page + 1)}
                  disabled={resolvedPagination.page >= resolvedPagination.totalPages}
                  className="relative inline-flex items-center rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition-all hover:bg-slate-50 hover:text-primary-600 disabled:opacity-40 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
